import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { VisiteTrattenute, riparti } from "../lib/visite_trattenute"

// «Chiedi all'AI» della pagina Preparazione (components/readiness/AskAI.js, 31a5bd1). Lo storico lo rende il server
// (readiness/tabs/_ask); qui lo stato che React teneva in history/loading:
// - Invio nel campo invia (preventDefault), Maiusc+Invio va a capo; «Invio» e le domande suggerite inviano la loro
//   domanda e svuotano il campo (send: setQuestion("")); una domanda vuota (dopo trim) non parte. Come l'originale,
//   Invio non guarda `loading` (solo il pulsante è disattivato durante una richiesta).
// - ask: la domanda e «Sto leggendo i documenti del progetto…» subito (modello «attesa», id tmp-<Date.now()>), poi
//   POST /app/progetto/:id/ask {question, bolla} con 120 s di tempo (timeout di axios). Il Turbo Stream della risposta
//   (qualunque stato) sostituisce la bolla con la voce salvata o con l'errore e «Riprova»; senza stream (rete, 500 di un
//   proxy) «Risposta non disponibile. Riprova.», scaduto il tempo «La risposta ha richiesto troppo tempo. Riprova.»
//   (modello «errore»). I testi entrano solo come testo.
// - «Riprova» rifà la domanda nella stessa bolla (stesso id); disattivato durante una richiesta o senza canEdit.
// - loading: pulsante disattivato con Loader2 al posto di Send, «Riprova» disattivati; come l'originale è uno solo per
//   tutte le richieste (la prima che finisce lo spegne). L'invito vuoto e le domande suggerite spariscono alla prima
//   domanda (history non è più vuota).
// - Scorrimento in fondo (scrollIntoView smooth) a ogni voce nuova o cambio di loading e quando il pannello si apre
//   (evento readiness:ask di readiness_controller.js); mai a pannello nascosto.
// - Una rilettura della pagina con morph (es. un'analisi finita, pannello_analisi_controller) non tocca il pannello
//   (turbo:before-morph-element sul suo elemento): la conversazione resta come lo stato di React; readiness_controller
//   lo riapre se era aperto. Una visita alla stessa pagina mentre una domanda è in volo aspetta la risposta e parte dopo
//   con action "replace" (lib/visite_trattenute.js, come dropzone_controller.js e turbo/refresh_dopo_invio.js). Le
//   visite verso altre pagine passano: fetch non si interrompe con una visita, la risposta si salva comunque (come la promessa dell'originale
//   sopravviveva al componente) e lo stream che arriva dopo non trova più la bolla.
const TEMPO = 120000
const MSG_TEMPO = "La risposta ha richiesto troppo tempo. Riprova."
const MSG_NON_DISPONIBILE = "Risposta non disponibile. Riprova."
const STREAM = "text/vnd.turbo-stream.html"
const PREFISSO = "ask-msg-"

export default class extends Controller {
  static targets = ["chat", "vuoto", "suggerite", "campo", "invia", "attesa", "icona", "messaggio", "riprova", "fine",
    "modelloAttesa", "modelloErrore"]
  // tempo: i 120 s di axios (le prove lo accorciano)
  static values = { url: String, canEdit: Boolean, tempo: { type: Number, default: TEMPO } }

  connect() {
    this.loading = false
    this.inVolo = 0
    this.visite = new VisiteTrattenute({ filtro: (url) => url === window.location.href })
  }

  disconnect() {
    this.visite.rilascia()
  }

  tasto(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      this.manda(this.campoTarget.value)
    }
  }

  invia() {
    if (this.hasCampoTarget) this.manda(this.campoTarget.value)
  }

  suggerita({ params: { testo } }) {
    this.manda(String(testo ?? ""))
  }

  riprova(event) {
    const msg = event.currentTarget.closest("[data-testid='ask-message']")
    if (!msg || this.loading || !this.canEditValue) return
    this.chiedi(msg.querySelector("[data-ask-domanda]")?.textContent ?? "", msg)
  }

  manda(q) {
    const text = (q || "").trim()
    if (!text) return
    if (this.hasCampoTarget) this.campoTarget.value = ""
    this.chiedi(text)
  }

  async chiedi(text, vecchia = null) {
    const id = vecchia ? vecchia.id.slice(PREFISSO.length) : `tmp-${Date.now()}`
    const attesa = this.bolla(this.modelloAttesaTarget, id, text)
    if (vecchia) vecchia.replaceWith(attesa)
    else this.fineTarget.before(attesa)
    this.vuotoTargets.forEach((v) => { v.hidden = true })
    this.suggeriteTargets.forEach((s) => s.remove())
    this.inVolo += 1
    this.visite.trattieni()
    this.carica(true)

    const ctrl = new AbortController()
    const timer = setTimeout(() => ctrl.abort(), this.tempoValue)
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    let errore = null
    try {
      const r = await fetch(this.urlValue, {
        method: "POST", credentials: "same-origin", signal: ctrl.signal,
        headers: { "Content-Type": "application/json", Accept: STREAM, ...(token ? { "X-CSRF-Token": token } : {}) },
        body: JSON.stringify({ question: text, bolla: id }),
      })
      const corpo = await r.text()
      if ((r.headers.get("Content-Type") || "").startsWith(STREAM)) Turbo.renderStreamMessage(corpo)
      else errore = MSG_NON_DISPONIBILE
    } catch (e) {
      errore = e?.name === "AbortError" ? MSG_TEMPO : MSG_NON_DISPONIBILE
    } finally {
      clearTimeout(timer)
    }
    if (errore) document.getElementById(PREFISSO + id)?.replaceWith(this.bolla(this.modelloErroreTarget, id, text, errore))
    this.inVolo -= 1
    this.carica(false)
    if (this.inVolo === 0) {
      const url = this.visite.rilascia()
      if (url) riparti(url)
    }
  }

  // Una bolla dal modello (template) con l'id, la domanda e l'errore come testo.
  bolla(modello, id, text, errore = null) {
    const el = modello.content.firstElementChild.cloneNode(true)
    el.id = PREFISSO + id
    el.querySelector("[data-ask-domanda]").textContent = text
    if (errore) el.querySelector("[data-ask-errore]").textContent = errore
    return el
  }

  carica(on) {
    this.loading = on
    if (this.hasInviaTarget) this.inviaTarget.disabled = on
    this.attesaTargets.forEach((el) => el.classList.toggle("hidden", !on))
    this.iconaTargets.forEach((el) => el.classList.toggle("hidden", on))
    this.riprovaTargets.forEach((b) => this.statoRiprova(b))
    this.scorri()
  }

  statoRiprova(b) {
    b.disabled = this.loading || !this.canEditValue
  }

  riprovaTargetConnected(b) {
    this.statoRiprova(b)
  }

  messaggioTargetConnected() {
    this.scorri()
  }

  scorri() {
    if (!this.hasFineTarget || this.element.closest("[hidden]")) return
    this.fineTarget.scrollIntoView({ behavior: "smooth" })
  }

  // La rilettura con morph lascia il pannello com'è (lo stato della conversazione).
  trattieni(event) {
    if (event.target === this.element) event.preventDefault()
  }
}
